import React, { Dispatch, SetStateAction } from "react";
import { Dialog, ButtonVariant } from "@merge-api/merge-javascript-shared";
import clsx from "clsx";
import CredentialChips from "./CredentialChips";
import PartnershipConfigurationFieldInput from "./PartnershipConfigurationFieldInput";
import find from "lodash/find";
import set from "lodash/set";
import {
  OrganizationConfigStatusField,
  OrganizationIntegrationSettings,
  PartnershipType,
} from "../../../../../../models/Entities";
import usePartnershipModal, { ConfigFieldMapValue } from "./hooks/usePartnershipModal";
import { Key } from "lucide-react";
import {
  NoMergeCredentialsText,
  MergeProvidedCredentialsText,
  CredentialChoices,
} from "./constants";

interface PartnershipCredentialModalProps {
  open: boolean;
  onModalClose: () => void;
  configStatusFields: OrganizationConfigStatusField[];
  isAlreadyCompleted: boolean;
  organizationIntegrationSettingsID: string;
  setOrganizationIntegrationSettings: Dispatch<SetStateAction<OrganizationIntegrationSettings>>;
  hasMergeCreds: boolean;
  partnershipType: string;
}

const PartnershipCredentialModal = ({
  open,
  onModalClose,
  configStatusFields,
  isAlreadyCompleted,
  organizationIntegrationSettingsID,
  setOrganizationIntegrationSettings,
  hasMergeCreds,
  partnershipType,
}: PartnershipCredentialModalProps) => {
  const {
    configFieldMap,
    setConfigFieldMap,
    height,
    selectedCredentialType,
    setSelectedCredentialType,
    deleteCredentialsLoading,
    deletePartnerCredentials,
    savePartnerCredentials,
    updatePartnershipCredentialsLoading: savePartnerCredentialsLoading,
  } = usePartnershipModal({
    organizationIntegrationSettingsID: organizationIntegrationSettingsID,
    configStatusFields: configStatusFields,
    onModalClose: onModalClose,
    setOrganizationIntegrationSettings: setOrganizationIntegrationSettings,
    hasMergeCreds: hasMergeCreds,
    partnershipType: partnershipType,
  });

  const missingRequiredField = find(configFieldMap, (configField: ConfigFieldMapValue) => {
    return (
      (configField.isOptional == false &&
        !configField.isComplete &&
        (configField.value == null || configField.value == "")) ||
      (configField.isModified && (configField.value == null || configField.value == ""))
    );
  });

  const initialCredsType =
    partnershipType == PartnershipType.ORGANIZATION_PARTNERSHIP
      ? CredentialChoices.CUSTOMER
      : CredentialChoices.MERGE;
  const needToSaveChanges =
    find(configFieldMap, (configFieldMap: ConfigFieldMapValue) => {
      return configFieldMap.isModified == true;
    }) || initialCredsType !== selectedCredentialType;

  const handleChipSelect = (value: string | number | boolean | null) => {
    if (typeof value == "string" || value == null) {
      setSelectedCredentialType(value);
    }
  };

  return (
    <Dialog
      preTitleIcon={<Key size={16} stroke="black" />}
      title="Credentials"
      onClose={onModalClose}
      variant="md"
      open={open}
      primaryButtonText="Save"
      primaryButtonVariant={ButtonVariant.PrimaryGray}
      primaryButtonLoading={savePartnerCredentialsLoading}
      primaryButtonDisabled={
        (missingRequiredField !== undefined &&
          selectedCredentialType == CredentialChoices.CUSTOMER) ||
        (isAlreadyCompleted && !needToSaveChanges)
      }
      onPrimaryButtonClick={savePartnerCredentials}
      secondaryButtonText={isAlreadyCompleted ? "Remove credentials" : "Close"}
      secondaryButtonVariant={
        isAlreadyCompleted ? ButtonVariant.DangerText : ButtonVariant.TextBlack
      }
      secondaryButtonLoading={isAlreadyCompleted && deleteCredentialsLoading}
      onSecondaryButtonClick={isAlreadyCompleted ? deletePartnerCredentials : onModalClose}
      bodyClassName="pt-6"
    >
      <div
        className={clsx(
          height,
          "mt-[-4px] transition-all ease duration-300 max-h-[304px] overflow-y-auto px-6",
          selectedCredentialType === CredentialChoices.CUSTOMER &&
            "[mask-image:linear-gradient(to_bottom,black_calc(100%-37px),transparent_100%)]",
        )}
      >
        <div
          className={clsx(
            "flex flex-col gap-4",
            selectedCredentialType === CredentialChoices.CUSTOMER ? "pb-[40px]" : "pb-6",
          )}
        >
          <div>
            {hasMergeCreds ? <>{MergeProvidedCredentialsText}</> : <>{NoMergeCredentialsText}</>}
          </div>
          {hasMergeCreds && (
            <CredentialChips
              selectedCredentialType={selectedCredentialType}
              onChipSelect={handleChipSelect}
            />
          )}
          {/* Partnership credential fields hold customer OAuth client IDs, secrets, signing keys and are thus masked at the field section (ph-no-capture for PostHog, data-fr-ignore for the Frigade Assistant DOM snapshot) */}
          <div
            className={clsx(
              "ph-no-capture transition duration-700 ease flex flex-col gap-4",
              selectedCredentialType !== CredentialChoices.CUSTOMER && "opacity-0 h-[0px]",
            )}
            data-fr-ignore
          >
            {configStatusFields.map((configStatusField) => (
              <PartnershipConfigurationFieldInput
                key={configStatusField.field_name}
                configField={configStatusField}
                configFieldMap={configFieldMap}
                handleFieldInput={(
                  fieldName: string,
                  fieldValue: string | Blob,
                  editting: boolean = false,
                ) => {
                  const fieldConfigToUpdate = configFieldMap[fieldName];
                  const updatedConfigMap = set(configFieldMap, `${fieldName}`, {
                    ...fieldConfigToUpdate,
                    ...{
                      value: fieldValue,
                      ...(editting ? { isModified: true } : {}),
                    },
                  });
                  setConfigFieldMap({ ...updatedConfigMap });
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default PartnershipCredentialModal;
